import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useSupportContext } from '../../context/SupportContext';
import axios from 'axios';
import {
  X,
  User,
  Shield,
  Clock,
  Bell,
  Settings,
  Search,
  ChevronDown,
  ChevronRight,
  Save,
  Eye,
  EyeOff,
  Lock,
  Unlock,
  Info,
  AlertTriangle,
  CheckCircle,
  Building2,
  Copy,
  KeyRound,
  Camera,
  Loader2,
  Wallet,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../../components/ui/sheet';
import { toast } from 'sonner';
import CopyPermissionsModal from './CopyPermissionsModal';
import EditUserPaymentsTab from './EditUserPaymentsTab';
import {
  validatePasswordPair,
  canActorManagePasswords,
  canActorManageTargetPassword,
  MIN_PASSWORD_LENGTH,
} from '../../utils/passwordValidation';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

// FundElite premium color palette
const colors = {
  amber500: '#F59E0B',
  amber100: '#FEF3C7',
  amber50: '#FFFBEB',
  emerald500: '#10B981',
  emerald100: '#D1FAE5',
  slate50: '#F8FAFC',
  slate100: '#F1F5F9',
  slate200: '#E2E8F0',
  slate500: '#64748B',
  slate700: '#334155',
  slate900: '#0F172A',
};

// Available roles
const USER_ROLES = [
  { value: 'owner', label: 'Agency Owner', description: 'Full access to agency settings' },
  { value: 'specialist', label: 'Specialist', description: 'Can manage assigned clients' },
  { value: 'staff', label: 'Staff', description: 'Limited access' },
  { value: 'self_repair_customer', label: 'Self-Repair Customer', description: 'Portal access only' },
  { value: 'managed_client', label: 'Managed Client', description: 'View only access' },
];

// Permission Groups (User-level)
const PERMISSION_GROUPS = [
  {
    key: 'ai_agents',
    name: 'AI Agents',
    permissions: [
      { key: 'ai_letter_generation', label: 'AI Letter Generation', description: 'Generate dispute letters using AI' },
      { key: 'ai_credit_analysis', label: 'AI Credit Analysis', description: 'AI-powered credit report analysis' },
      { key: 'ai_suggestions', label: 'AI Suggestions', description: 'Get AI-powered recommendations' },
    ],
  },
  {
    key: 'account_settings',
    name: 'Account Settings',
    permissions: [
      { key: 'manage_profile', label: 'Manage Profile', description: 'Edit own profile information' },
      { key: 'manage_password', label: 'Change Password', description: 'Change account password' },
      { key: 'manage_notifications', label: 'Notification Settings', description: 'Manage notification preferences' },
      { key: 'view_billing', label: 'View Billing', description: 'View billing and payment history' },
    ],
  },
  {
    key: 'credit_builder_tools',
    name: 'Credit Builder Tools',
    permissions: [
      { key: 'credit_builder_view', label: 'Credit Builder View', description: 'View credit builder dashboard' },
      { key: 'utilization_strategy_planner', label: 'Utilization Strategy Planner', description: 'Manage credit utilization' },
      { key: 'payment_planner', label: 'Payment Planner', description: 'Plan payment schedules' },
      { key: 'credit_builder_full', label: 'Credit Builder Full', description: 'Full credit builder access' },
    ],
  },
];

// Portal Sections (Agency-wide)
const PORTAL_SECTIONS = [
  { key: 'dashboard', name: 'Dashboard', description: 'Main portal dashboard and overview' },
  { key: 'credit_report', name: 'Credit Report', description: 'View and analyze credit reports' },
  { key: 'dispute_wizard', name: 'Dispute Wizard', description: 'Create and manage disputes' },
  { key: 'my_disputes', name: 'My Disputes', description: 'View dispute history and status' },
  { key: 'my_documents', name: 'My Documents', description: 'Upload and manage documents' },
  { key: 'credit_builder_section', name: 'Credit Builder Section', description: 'Access credit building tools' },
  { key: 'blueprints', name: 'Blueprints', description: 'View credit improvement blueprints' },
  { key: 'academy', name: 'Academy', description: 'Educational resources and courses' },
  { key: 'settings', name: 'Settings', description: 'Portal settings and preferences' },
];

// Access modes with styling
const ACCESS_MODES = [
  { value: 'hidden', label: 'Hidden', bg: 'bg-slate-100', text: 'text-slate-600', icon: EyeOff },
  { value: 'locked', label: 'Locked', bg: 'bg-amber-100', text: 'text-amber-700', icon: Lock },
  { value: 'read_only', label: 'Read Only', bg: 'bg-blue-100', text: 'text-blue-700', icon: Eye },
  { value: 'full_access', label: 'Full Access', bg: 'bg-emerald-100', text: 'text-emerald-700', icon: Unlock },
];

/**
 * Segmented Control - Pill switcher component
 */
const SegmentedControl = ({ options, value, onChange }) => (
  <div 
    className="inline-flex p-1 rounded-full border"
    style={{ backgroundColor: colors.slate100, borderColor: colors.slate200 }}
  >
    {options.map(option => (
      <button
        key={option.value}
        onClick={() => onChange(option.value)}
        className={`
          px-5 py-2 text-sm font-medium rounded-full transition-all duration-200
          ${value === option.value 
            ? 'bg-white text-slate-900 shadow-sm' 
            : 'text-slate-500 hover:text-slate-700'
          }
        `}
        data-testid={`segment-${option.value}`}
      >
        {option.label}
      </button>
    ))}
  </div>
);

/**
 * Access Mode Badge
 */
const AccessModeBadge = ({ mode }) => {
  const config = ACCESS_MODES.find(m => m.value === mode) || ACCESS_MODES[0];
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
      <Icon size={12} />
      {config.label}
    </span>
  );
};

/**
 * Collapsible Permission Group
 */
const PermissionGroup = ({ 
  group, 
  isExpanded, 
  onToggle, 
  permissions,
  onPermissionChange,
  onGroupToggle,
  searchQuery,
}) => {
  const filteredPermissions = group.permissions.filter(perm =>
    perm.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
    perm.description.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  if (searchQuery && filteredPermissions.length === 0) return null;
  
  const allEnabled = filteredPermissions.every(p => permissions[p.key]);
  const someEnabled = filteredPermissions.some(p => permissions[p.key]) && !allEnabled;
  
  return (
    <div className="border-t border-slate-100 py-4" data-testid={`perm-group-${group.key}`}>
      <button
        className="w-full flex items-center justify-between text-left group"
        onClick={onToggle}
      >
        <div className="flex items-center gap-3">
          <Switch 
            checked={allEnabled}
            onCheckedChange={(checked) => onGroupToggle(group.key, checked)}
            onClick={(e) => e.stopPropagation()}
            className={someEnabled ? 'data-[state=unchecked]:bg-emerald-200' : ''}
          />
          <span className="font-semibold text-slate-900">{group.name}</span>
          <span className="text-xs text-slate-400">
            {filteredPermissions.filter(p => permissions[p.key]).length}/{filteredPermissions.length}
          </span>
        </div>
        <span className="flex items-center gap-2 text-xs text-slate-400 group-hover:text-slate-600">
          {isExpanded ? 'Collapse' : 'Expand'}
          {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </span>
      </button>
      
      {isExpanded && (
        <div className="mt-3 grid grid-cols-2 gap-2 ml-11">
          {filteredPermissions.map(perm => (
            <label 
              key={perm.key}
              className="flex items-center gap-2.5 p-2 rounded-lg hover:bg-slate-50 cursor-pointer"
            >
              <Checkbox
                checked={permissions[perm.key] || false}
                onCheckedChange={(checked) => onPermissionChange(perm.key, checked)}
              />
              <span className="text-sm text-slate-700">{perm.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Portal Access Row
 */
const PortalAccessRow = ({ section, accessMode, onChange }) => {
  const modeConfig = ACCESS_MODES.find(m => m.value === accessMode) || ACCESS_MODES[3];
  
  return (
    <tr className="border-b border-slate-100 hover:bg-slate-50/50">
      <td className="py-3 px-4">
        <div>
          <p className="font-medium text-slate-900">{section.name}</p>
          <p className="text-xs text-slate-400 mt-0.5">{section.description}</p>
        </div>
      </td>
      <td className="py-3 px-4">
        <Select value={accessMode} onValueChange={onChange}>
          <SelectTrigger className="w-40 h-9 rounded-xl border-slate-200">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ACCESS_MODES.map(mode => (
              <SelectItem key={mode.value} value={mode.value}>
                <div className="flex items-center gap-2">
                  <mode.icon size={14} className={mode.text} />
                  {mode.label}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>
      <td className="py-3 px-4">
        <AccessModeBadge mode={accessMode} />
      </td>
    </tr>
  );
};

/**
 * User Permissions Section
 */
const UserPermissionsSection = ({
  userRole,
  setUserRole,
  permissions,
  setPermissions,
  restrictDataVisibility,
  setRestrictDataVisibility,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedGroups, setExpandedGroups] = useState({ ai_agents: true });
  
  const toggleGroup = (groupKey) => {
    setExpandedGroups(prev => ({ ...prev, [groupKey]: !prev[groupKey] }));
  };
  
  const handlePermissionChange = (permKey, value) => {
    setPermissions(prev => ({ ...prev, [permKey]: value }));
  };
  
  const handleGroupToggle = (groupKey, enabled) => {
    const group = PERMISSION_GROUPS.find(g => g.key === groupKey);
    if (!group) return;
    
    const updates = {};
    group.permissions.forEach(p => {
      updates[p.key] = enabled;
    });
    setPermissions(prev => ({ ...prev, ...updates }));
  };
  
  return (
    <div className="space-y-6" data-testid="user-permissions-section">
      {/* Role & Search Row */}
      <div className="flex items-start gap-4">
        {/* User Role */}
        <div className="flex-1">
          <label className="block text-sm font-medium text-slate-700 mb-2">User Role</label>
          <Select value={userRole} onValueChange={setUserRole}>
            <SelectTrigger className="h-11 rounded-xl border-slate-200" data-testid="user-role-select">
              <SelectValue placeholder="Select role..." />
            </SelectTrigger>
            <SelectContent>
              {USER_ROLES.map(role => (
                <SelectItem key={role.value} value={role.value}>
                  <div>
                    <span className="font-medium">{role.label}</span>
                    <span className="text-xs text-slate-400 ml-2">{role.description}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {/* Search Permissions */}
        <div className="flex-1">
          <label className="block text-sm font-medium text-slate-700 mb-2">Search Permissions</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search permissions..."
              className="pl-9 h-11 rounded-xl border-slate-200"
              data-testid="search-permissions-input"
            />
          </div>
        </div>
      </div>
      
      {/* Restrict Data Visibility */}
      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-100">
        <div>
          <p className="font-medium text-slate-900">Restrict data visibility to only assigned data</p>
          <p className="text-sm text-slate-500 mt-0.5">User will only see clients and cases assigned to them</p>
        </div>
        <Switch 
          checked={restrictDataVisibility}
          onCheckedChange={setRestrictDataVisibility}
          data-testid="restrict-data-switch"
        />
      </div>
      
      {/* Permission Groups */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {PERMISSION_GROUPS.map(group => (
          <PermissionGroup
            key={group.key}
            group={group}
            isExpanded={expandedGroups[group.key]}
            onToggle={() => toggleGroup(group.key)}
            permissions={permissions}
            onPermissionChange={handlePermissionChange}
            onGroupToggle={handleGroupToggle}
            searchQuery={searchQuery}
          />
        ))}
      </div>
    </div>
  );
};

/**
 * Portal Access Section (Agency-wide)
 */
const PortalAccessSection = ({
  portalAccess,
  setPortalAccess,
  agencyName,
}) => {
  return (
    <div className="space-y-4" data-testid="portal-access-section">
      {/* Scope Indicator */}
      <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
        <Building2 className="text-amber-600 mt-0.5" size={20} />
        <div>
          <div className="flex items-center gap-2">
            <h4 className="font-semibold text-amber-900">Agency-wide Settings</h4>
            <Badge className="bg-amber-100 text-amber-700 border-amber-200">Agency-wide</Badge>
          </div>
          <p className="text-sm text-amber-700 mt-1">
            These settings affect <strong>all self-serve users</strong> in {agencyName || 'this agency'}.
            Changes here are not limited to this individual user.
          </p>
        </div>
      </div>
      
      {/* Portal Sections Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
          <h4 className="font-semibold text-slate-900">Portal Section Access</h4>
          <p className="text-xs text-slate-500 mt-0.5">Control which sections are visible in the self-serve portal</p>
        </div>
        
        <table className="w-full">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">Portal Section</th>
              <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">Access Mode</th>
              <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">State</th>
            </tr>
          </thead>
          <tbody>
            {PORTAL_SECTIONS.map(section => (
              <PortalAccessRow
                key={section.key}
                section={section}
                accessMode={portalAccess[section.key] || 'full_access'}
                onChange={(value) => setPortalAccess(prev => ({ ...prev, [section.key]: value }))}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/**
 * EditUserDrawer - Main component
 */
const EditUserDrawer = ({
  isOpen,
  onClose,
  user,
  agencyId,
  agencyName,
  onSave,
}) => {
  const { token, user: currentUser } = useAuth();
  const { isInSupportMode } = useSupportContext();
  const [activeTab, setActiveTab] = useState('user_info');
  const [activePermSection, setActivePermSection] = useState('user_permissions');
  const [saving, setSaving] = useState(false);
  
  // User Info state
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userPhone, setUserPhone] = useState('');

  // Password management (isolated from main save form)
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [updatingPassword, setUpdatingPassword] = useState(false);
  const [sendingReset, setSendingReset] = useState(false);
  const [resetLinkSent, setResetLinkSent] = useState(false);
  
  // Permissions state
  const [userRole, setUserRole] = useState('self_repair_customer');
  const [permissions, setPermissions] = useState({});
  const [restrictDataVisibility, setRestrictDataVisibility] = useState(false);
  const [portalAccess, setPortalAccess] = useState({});
  
  const headers = { Authorization: `Bearer ${token}` };
  
  // Initialize state from user prop
  useEffect(() => {
    if (user) {
      setUserName(user.name || '');
      setUserEmail(user.email || '');
      setUserPhone(user.phone || '');
      setUserRole(user.role || 'self_repair_customer');
      setPermissions(user.permissions || {});
      setRestrictDataVisibility(user.restrict_data_visibility || false);
      setPortalAccess(user.portal_access || {});
    }
  }, [user]);

  const clearPasswordFields = () => {
    setNewPassword('');
    setConfirmPassword('');
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    setResetLinkSent(false);
  };

  useEffect(() => {
    if (!isOpen) {
      clearPasswordFields();
    }
  }, [isOpen]);

  useEffect(() => {
    clearPasswordFields();
  }, [user?.user_id]);

  const showPasswordManagement = useMemo(() => {
    if (!currentUser || !user) return false;
    if (!canActorManagePasswords(currentUser.role, currentUser.agency_id, agencyId)) {
      return false;
    }
    return canActorManageTargetPassword(user.role, currentUser.role);
  }, [currentUser, user, agencyId]);

  const passwordValidation = useMemo(
    () => validatePasswordPair(newPassword, confirmPassword),
    [newPassword, confirmPassword]
  );

  const passwordEndpoints = useMemo(() => {
    if (!user?.user_id) return null;
    const usePortalStaff =
      ['owner', 'specialist'].includes(currentUser?.role) ||
      (Boolean(agencyId) && isInSupportMode);
    if (usePortalStaff) {
      return {
        update: `${API}/portal/staff/users/${user.user_id}/password`,
        reset: `${API}/portal/staff/users/${user.user_id}/send-password-reset`,
      };
    }
    return {
      update: `${API}/admin-ops/users/${user.user_id}/password`,
      reset: `${API}/admin-ops/users/${user.user_id}/send-password-reset`,
    };
  }, [user?.user_id, currentUser?.role, agencyId, isInSupportMode]);
  
  // Load agency portal access settings
  useEffect(() => {
    if (isOpen && agencyId && activeTab === 'roles_permissions') {
      loadAgencyPortalAccess();
    }
  }, [isOpen, agencyId, activeTab]);
  
  const loadAgencyPortalAccess = async () => {
    try {
      const res = await axios.get(
        `${API}/membership-admin/section-access/agency/${agencyId}`,
        { headers }
      );
      if (res.data.section_access_overrides) {
        setPortalAccess(res.data.section_access_overrides);
      }
    } catch (err) {
      console.error('Failed to load portal access:', err);
      // Use defaults
      const defaults = {};
      PORTAL_SECTIONS.forEach(s => { defaults[s.key] = 'full_access'; });
      setPortalAccess(defaults);
    }
  };
  
  const handleSave = async () => {
    setSaving(true);
    try {
      // Save user info and permissions
      await axios.put(
        `${API}/admin/users/${user.user_id}`,
        {
          name: userName,
          email: userEmail,
          phone: userPhone,
          role: userRole,
          permissions,
          restrict_data_visibility: restrictDataVisibility,
        },
        { headers }
      );
      
      // Save agency portal access (if in that section)
      if (Object.keys(portalAccess).length > 0) {
        await axios.put(
          `${API}/membership-admin/section-access/agency/${agencyId}`,
          { section_access_overrides: portalAccess },
          { headers }
        );
      }
      
      toast.success('User settings saved successfully');
      if (onSave) onSave();
      onClose();
    } catch (err) {
      console.error('Save error:', err);
      toast.error(err.response?.data?.detail || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePassword = async () => {
    if (!passwordEndpoints || !passwordValidation.valid) return;
    setUpdatingPassword(true);
    try {
      await axios.post(
        passwordEndpoints.update,
        { new_password: newPassword },
        { headers }
      );
      toast.success('Password updated successfully');
      clearPasswordFields();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update password');
    } finally {
      setUpdatingPassword(false);
    }
  };

  const handleSendPasswordReset = async () => {
    if (!passwordEndpoints) return;
    setSendingReset(true);
    setResetLinkSent(false);
    try {
      await axios.post(passwordEndpoints.reset, {}, { headers });
      toast.success('Password reset link sent to user email.');
      setResetLinkSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send password reset link');
    } finally {
      setSendingReset(false);
    }
  };
  
  const walletApiBase = useMemo(() => {
    const usePortalStaff =
      ['owner', 'specialist'].includes(currentUser?.role) ||
      (Boolean(agencyId) && isInSupportMode);
    return usePortalStaff
      ? `${API}/portal/staff/users`
      : `${API}/admin-ops/users`;
  }, [currentUser?.role, agencyId, isInSupportMode]);

  const drawerTabs = [
    { key: 'user_info', label: 'User Info', icon: User },
    { key: 'roles_permissions', label: 'Roles & Permissions', icon: Shield },
    { key: 'availability', label: 'Availability', icon: Clock },
    { key: 'notifications', label: 'Notifications', icon: Bell },
    { key: 'payments', label: 'Payments', icon: Wallet },
    { key: 'advanced', label: 'Advanced Settings', icon: Settings },
  ];
  
  // Copy permissions modal state
  const [showCopyModal, setShowCopyModal] = useState(false);
  
  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent 
        className="w-full p-0 flex flex-col" 
        side="right"
        style={{ maxWidth: 'min(1120px, 76vw)', width: '100%' }}
        data-testid="edit-user-drawer"
      >
        {/* Header */}
        <SheetHeader className="px-6 py-4 border-b border-slate-200 bg-white">
          <div className="flex items-center justify-between">
            <SheetTitle className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <User className="text-amber-600" size={20} />
              </div>
              <div>
                <span className="text-lg font-semibold text-slate-900">Edit User</span>
                <p className="text-sm text-slate-500">{user?.email}</p>
                {user?.user_id && (
                  <div className="flex items-center gap-2 mt-1">
                    <code className="text-xs font-mono bg-slate-100 px-2 py-0.5 rounded text-slate-700">
                      {user.user_id}
                    </code>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-xs"
                      onClick={() => {
                        navigator.clipboard.writeText(user.user_id);
                        toast.success('User ID copied');
                      }}
                    >
                      <Copy size={12} className="mr-1" />
                      Copy ID
                    </Button>
                  </div>
                )}
              </div>
            </SheetTitle>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X size={20} />
            </Button>
          </div>
        </SheetHeader>
        
        {/* Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Sidebar Navigation */}
          <div className="w-48 border-r border-slate-200 bg-slate-50 py-4">
            {drawerTabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`
                  w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-left transition-colors
                  ${activeTab === tab.key 
                    ? 'bg-white border-r-2 border-amber-500 text-amber-600 font-medium' 
                    : 'text-slate-600 hover:bg-slate-100'
                  }
                `}
                data-testid={`drawer-tab-${tab.key}`}
              >
                <tab.icon size={16} />
                {tab.label}
              </button>
            ))}
          </div>
          
          {/* Main Content Area */}
          <div className="flex-1 overflow-y-auto p-6 bg-white">
            {activeTab === 'user_info' && (
              <div className="space-y-4" data-testid="user-info-tab">
                <h3 className="text-lg font-semibold text-slate-900">User Information</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Name</label>
                    <Input
                      value={userName}
                      onChange={(e) => setUserName(e.target.value)}
                      className="h-11 rounded-xl"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                    <Input
                      type="email"
                      value={userEmail}
                      onChange={(e) => setUserEmail(e.target.value)}
                      className="h-11 rounded-xl"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Phone</label>
                    <Input
                      value={userPhone}
                      onChange={(e) => setUserPhone(e.target.value)}
                      className="h-11 rounded-xl"
                    />
                  </div>

                  {showPasswordManagement && (
                    <div
                      className="pt-6 mt-2 border-t border-slate-200 space-y-4"
                      data-testid="password-management-section"
                    >
                      <div className="flex items-center gap-2">
                        <KeyRound className="text-amber-600" size={18} />
                        <h4 className="text-base font-semibold text-slate-900">Password Management</h4>
                      </div>
                      <p className="text-sm text-slate-500">
                        Set a new password or email the user a secure reset link. Existing passwords are never shown.
                      </p>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">New Password</label>
                        <div className="relative">
                          <Input
                            type={showNewPassword ? 'text' : 'password'}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            autoComplete="new-password"
                            placeholder={`At least ${MIN_PASSWORD_LENGTH} characters`}
                            className="h-11 rounded-xl pr-10"
                            data-testid="new-password-input"
                          />
                          <button
                            type="button"
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                            onClick={() => setShowNewPassword((v) => !v)}
                            aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                          >
                            {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                          </button>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Confirm Password</label>
                        <div className="relative">
                          <Input
                            type={showConfirmPassword ? 'text' : 'password'}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            autoComplete="new-password"
                            placeholder="Re-enter new password"
                            className="h-11 rounded-xl pr-10"
                            data-testid="confirm-password-input"
                          />
                          <button
                            type="button"
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                            onClick={() => setShowConfirmPassword((v) => !v)}
                            aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                          >
                            {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                          </button>
                        </div>
                      </div>

                      {passwordValidation.errors.length > 0 && (newPassword || confirmPassword) && (
                        <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                          {passwordValidation.errors.map((message) => (
                            <p key={message}>{message}</p>
                          ))}
                        </div>
                      )}

                      <div className="flex flex-wrap gap-3">
                        <Button
                          type="button"
                          onClick={handleUpdatePassword}
                          disabled={!passwordValidation.valid || updatingPassword}
                          className="bg-amber-600 hover:bg-amber-700"
                          data-testid="update-password-btn"
                        >
                          {updatingPassword ? (
                            <>
                              <Loader2 size={16} className="mr-2 animate-spin" />
                              Updating...
                            </>
                          ) : (
                            'Update Password'
                          )}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleSendPasswordReset}
                          disabled={sendingReset}
                          className="border-slate-200"
                          data-testid="send-reset-link-btn"
                        >
                          {sendingReset ? (
                            <>
                              <Loader2 size={16} className="mr-2 animate-spin" />
                              Sending...
                            </>
                          ) : (
                            'Send Forgot Password Link'
                          )}
                        </Button>
                      </div>

                      {resetLinkSent && (
                        <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2">
                          Password reset link sent to user email.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {activeTab === 'roles_permissions' && (
              <div className="space-y-6" data-testid="roles-permissions-tab">
                {/* Header with Copy Permissions */}
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">Roles & Permissions</h3>
                    <p className="text-sm text-slate-500">Configure user access and agency portal settings</p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setShowCopyModal(true)}
                    className="text-amber-600 border-amber-200 hover:bg-amber-50"
                    data-testid="copy-permissions-btn"
                  >
                    <Copy size={16} className="mr-2" />
                    Copy Permissions
                  </Button>
                </div>
                
                {/* Access Order Note */}
                <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-xl text-sm">
                  <Info className="text-blue-500 flex-shrink-0" size={16} />
                  <span className="text-blue-700">
                    <strong>Access order:</strong> Plan entitlement → Agency portal access → User permissions
                  </span>
                </div>
                
                {/* Segmented Control */}
                <div className="flex justify-center">
                  <SegmentedControl
                    options={[
                      { value: 'user_permissions', label: 'User Permissions' },
                      { value: 'portal_access', label: 'Portal Access (Agency-wide)' },
                    ]}
                    value={activePermSection}
                    onChange={setActivePermSection}
                  />
                </div>
                
                {/* Section Content */}
                {activePermSection === 'user_permissions' ? (
                  <UserPermissionsSection
                    userRole={userRole}
                    setUserRole={setUserRole}
                    permissions={permissions}
                    setPermissions={setPermissions}
                    restrictDataVisibility={restrictDataVisibility}
                    setRestrictDataVisibility={setRestrictDataVisibility}
                  />
                ) : (
                  <PortalAccessSection
                    portalAccess={portalAccess}
                    setPortalAccess={setPortalAccess}
                    agencyName={agencyName}
                  />
                )}
              </div>
            )}
            
            {activeTab === 'availability' && (
              <div className="text-center py-12 text-slate-400" data-testid="availability-tab">
                <Clock size={48} className="mx-auto mb-4 opacity-50" />
                <p>Availability settings coming soon</p>
              </div>
            )}
            
            {activeTab === 'notifications' && (
              <div className="text-center py-12 text-slate-400" data-testid="notifications-tab">
                <Bell size={48} className="mx-auto mb-4 opacity-50" />
                <p>Notification settings coming soon</p>
              </div>
            )}

            {activeTab === 'payments' && (
              <EditUserPaymentsTab
                user={user}
                agencyId={agencyId}
                currentUser={currentUser}
                apiBase={walletApiBase}
                headers={headers}
              />
            )}
            
            {activeTab === 'advanced' && (
              <div className="text-center py-12 text-slate-400" data-testid="advanced-tab">
                <Settings size={48} className="mx-auto mb-4 opacity-50" />
                <p>Advanced settings coming soon</p>
              </div>
            )}
          </div>
        </div>
        
        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50">
          <div className="flex items-center justify-between">
            <p className="text-xs text-slate-500">
              Saves both User Permissions and Portal Access changes
            </p>
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button 
                onClick={handleSave}
                disabled={saving}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="save-user-btn"
              >
                {saving ? (
                  <>Saving...</>
                ) : (
                  <>
                    <Save size={16} className="mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </SheetContent>
      
      {/* Copy Permissions Modal */}
      {showCopyModal && (
        <CopyPermissionsModal
          isOpen={showCopyModal}
          onClose={() => setShowCopyModal(false)}
          targetUser={user}
          agencyId={agencyId}
          onSuccess={() => {
            setShowCopyModal(false);
            // Reload user permissions
            loadAgencyPortalAccess();
          }}
        />
      )}
    </Sheet>
  );
};

export default EditUserDrawer;
